import { z } from 'zod';
import { createPaginatedResponseSchema, IdParamsSchema, MetadataSchema, PaginationQuerySchema, } from '../common/index.js';
export const EnvironmentTypeSchema = z.enum([
    'development',
    'staging',
    'production',
]);
export const EnvironmentStatusSchema = z.enum([
    'active',
    'inactive',
    'archived',
]);
export const EnvironmentSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    name: z.string().min(1).max(100),
    type: EnvironmentTypeSchema,
    slug: z.string().optional(),
    description: z.string().max(500).nullable().optional(),
    is_default: z.boolean().default(false),
    settings: z.record(z.string(), z.unknown()).optional(),
    color: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/)
        .optional(),
    icon: z.string().max(50).optional(),
    api_rate_limit: z.number().int().min(0).default(1000).optional(),
    webhook_url: z.string().url().nullable().optional(),
    status: EnvironmentStatusSchema.default('active'),
    metadata: MetadataSchema.optional(),
    created_by: z.string().optional(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const CreateEnvironmentSchema = z.object({
    tenant_id: z.string(),
    name: z.string().min(1, 'Environment name is required').max(100),
    type: EnvironmentTypeSchema.default('development'),
    slug: z.string().optional(),
    description: z.string().max(500).optional(),
    is_default: z.boolean().default(false).optional(),
    settings: z.record(z.string(), z.unknown()).optional(),
    color: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/)
        .optional(),
    icon: z.string().max(50).optional(),
    api_rate_limit: z.number().int().min(0).default(1000).optional(),
    webhook_url: z.string().url().optional(),
    metadata: MetadataSchema.optional(),
});
export const UpdateEnvironmentSchema = z.object({
    name: z.string().min(1).max(100).optional(),
    type: EnvironmentTypeSchema.optional(),
    description: z.string().max(500).optional().nullable(),
    is_default: z.boolean().optional(),
    color: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/)
        .optional()
        .nullable(),
    icon: z.string().max(50).optional().nullable(),
    api_rate_limit: z.number().int().min(0).optional(),
    webhook_url: z.string().url().optional().nullable(),
    status: EnvironmentStatusSchema.optional(),
    metadata: MetadataSchema.optional(),
});
export const SetDefaultEnvironmentSchema = z.object({
    environment_id: z.string(),
});
export const CloneEnvironmentSchema = z.object({
    source_environment_id: z.string(),
    new_name: z.string().min(1).max(100),
    new_type: EnvironmentTypeSchema,
    copy_data: z.boolean().default(false),
    copy_agents: z.boolean().default(true),
    copy_signals: z.boolean().default(true),
    copy_features: z.boolean().default(true),
    copy_plans: z.boolean().default(false),
});
export const EnvironmentQuerySchema = PaginationQuerySchema.extend({
    tenant_id: z.string().optional(),
    type: EnvironmentTypeSchema.optional(),
    status: EnvironmentStatusSchema.optional(),
    is_default: z.boolean().optional(),
    search: z.string().optional(),
    sort: z
        .enum(['name', 'type', 'created_at', 'updated_at'])
        .default('created_at')
        .optional(),
    order: z.enum(['asc', 'desc']).default('desc').optional(),
});
export const EnvironmentStatsSchema = z.object({
    environment_id: z.string(),
    agents_count: z.number().int().min(0),
    signals_count: z.number().int().min(0),
    features_count: z.number().int().min(0),
    plans_count: z.number().int().min(0),
    customers_count: z.number().int().min(0),
    contracts_count: z.number().int().min(0),
    total_api_calls: z.number().int().min(0),
    last_api_call_at: z.string().datetime().nullable().optional(),
});
export const EnvironmentResponseSchema = EnvironmentSchema.extend({
    stats: EnvironmentStatsSchema.optional(),
});
export const CloneEnvironmentResponseSchema = z.object({
    environment: EnvironmentResponseSchema,
    message: z.string(),
    items_copied: z.object({
        agents: z.number().int(),
        signals: z.number().int(),
        features: z.number().int(),
        plans: z.number().int(),
    }),
    success: z.boolean(),
});
export const PaginatedEnvironmentsResponseSchema = createPaginatedResponseSchema(EnvironmentResponseSchema);
export const EnvironmentParamsSchema = IdParamsSchema;
//# sourceMappingURL=index.js.map